/**
 * Speech utility functions for natural, human-like text-to-speech
 * Handles number conversion, greetings, and text preprocessing
 */

/**
 * Gets a time-based greeting based on local time
 * Morning (5:00 - 11:59) → "Good morning"
 * Afternoon (12:00 - 17:59) → "Good afternoon"
 * Evening/Night (18:00 - 4:59) → "Good evening"
 */
export function getTimeBasedGreeting(): string {
  const hour = new Date().getHours();
  
  if (hour >= 5 && hour < 12) {
    return 'Good morning';
  } else if (hour >= 12 && hour < 18) {
    return 'Good afternoon';
  } else {
    return 'Good evening';
  }
}

/**
 * Converts a number to its spoken word representation
 * Handles integers and decimals up to billions
 * Examples:
 * - 12000 → "twelve thousand"
 * - 3500000 → "three point five million"
 * - 1234567 → "one million two hundred thirty-four thousand five hundred sixty-seven"
 */
export function numberToWords(num: number): string {
  const ones = [
    '', 'one', 'two', 'three', 'four', 'five', 'six', 'seven', 'eight', 'nine',
    'ten', 'eleven', 'twelve', 'thirteen', 'fourteen', 'fifteen', 'sixteen',
    'seventeen', 'eighteen', 'nineteen'
  ];
  
  const tens = [
    '', '', 'twenty', 'thirty', 'forty', 'fifty', 'sixty', 'seventy', 'eighty', 'ninety'
  ];
  
  if (num === 0) return 'zero';
  if (num < 0) return 'negative ' + numberToWords(Math.abs(num));
  
  // Handle decimal numbers
  if (num % 1 !== 0) {
    const parts = num.toString().split('.');
    const integerPart = parseInt(parts[0], 10);
    const decimalPart = parts[1];
    
    let result = numberToWords(integerPart);
    
    if (decimalPart) {
      // For decimals, read digit by digit
      const decimalWords = decimalPart
        .split('')
        .map((d: string) => ones[parseInt(d, 10)])
        .join(' ');
      result += ' point ' + decimalWords;
    }
    
    return result;
  }
  
  // Handle integers
  if (num < 20) return ones[num];
  
  if (num < 100) {
    const ten = Math.floor(num / 10);
    const one = num % 10;
    return one > 0 ? `${tens[ten]}-${ones[one]}` : tens[ten];
  }
  
  if (num < 1000) {
    const hundred = Math.floor(num / 100);
    const remainder = num % 100;
    if (remainder === 0) return `${ones[hundred]} hundred`;
    return `${ones[hundred]} hundred and ${numberToWords(remainder)}`;
  }
  
  if (num < 1000000) {
    const thousand = Math.floor(num / 1000);
    const remainder = num % 1000;
    if (remainder === 0) return `${numberToWords(thousand)} thousand`;
    return `${numberToWords(thousand)} thousand ${numberToWords(remainder)}`;
  }
  
  if (num < 1000000000) {
    const million = Math.floor(num / 1000000);
    const remainder = num % 1000000;
    
    // For large numbers, use decimal notation when appropriate
    // e.g., 3500000 → "three point five million"
    if (remainder === 0) {
      return `${numberToWords(million)} million`;
    }
    
    // Check if we should use decimal notation (e.g., 3.5 million instead of 3 million 500 thousand)
    // This is more natural for numbers like 3.5M, 2.5M, etc.
    const decimalValue = num / 1000000;
    if (decimalValue < 10 && remainder >= 100000 && remainder < 900000) {
      // Check if it's a "nice" decimal like .5, .25, .75
      const decimalStr = decimalValue.toFixed(2);
      const parts = decimalStr.split('.');
      const whole = parseInt(parts[0], 10);
      const decimalPart = parseInt(parts[1], 10);
      
      // Handle .5, .25, .75 cases
      if (decimalPart === 50) {
        return `${numberToWords(whole)} point five million`;
      } else if (decimalPart === 25) {
        return `${numberToWords(whole)} point twenty-five million`;
      } else if (decimalPart === 75) {
        return `${numberToWords(whole)} point seventy-five million`;
      } else if (decimalPart % 10 === 0 && decimalPart < 100) {
        // Handle .1, .2, .3, etc. (tenths)
        const tenth = decimalPart / 10;
        return `${numberToWords(whole)} point ${numberToWords(tenth)} million`;
      }
    }
    
    // Otherwise, use full notation
    if (remainder < 1000) {
      return `${numberToWords(million)} million ${numberToWords(remainder)}`;
    } else {
      const thousand = Math.floor(remainder / 1000);
      const thousandRemainder = remainder % 1000;
      if (thousandRemainder === 0) {
        return `${numberToWords(million)} million ${numberToWords(thousand)} thousand`;
      } else {
        return `${numberToWords(million)} million ${numberToWords(thousand)} thousand ${numberToWords(thousandRemainder)}`;
      }
    }
  }
  
  // For billions and above, use simpler notation
  const billion = Math.floor(num / 1000000000);
  const remainder = num % 1000000000;
  if (remainder === 0) {
    return `${numberToWords(billion)} billion`;
  }
  
  // For very large numbers, use decimal notation when appropriate
  const decimalValue = num / 1000000000;
  if (decimalValue < 10 && remainder < 500000000) {
    const decimalStr = decimalValue.toFixed(1);
    if (!decimalStr.endsWith('.0')) {
      const whole = Math.floor(decimalValue);
      const decimal = Math.round((decimalValue - whole) * 10);
      if (decimal === 5) {
        return `${numberToWords(whole)} point five billion`;
      }
    }
  }
  
  return `${numberToWords(billion)} billion ${numberToWords(remainder)}`;
}

/**
 * Converts a single digit to its word representation
 */
function digitToWord(digit: string): string {
  const digits = ['zero', 'one', 'two', 'three', 'four', 'five', 'six', 'seven', 'eight', 'nine'];
  const num = parseInt(digit, 10);
  return isNaN(num) ? digit : (digits[num] || digit);
}

/**
 * Preprocesses text to convert numbers into natural spoken words
 * Enhanced for real estate metrics and context-aware narration
 * Handles:
 * - Real estate metric abbreviations (YoY, MoM, QoQ)
 * - Symbols (~, +, -) with context
 * - Standalone numbers (e.g., "12000" → "twelve thousand")
 * - Numbers with units (e.g., "1.25M" → "one point two five million")
 * - Percentages with direction (e.g., "+5%" → "up by five percent")
 * - Years (e.g., "2024" → "twenty twenty-four")
 * - Ordinal numbers (e.g., "1st" → "first")
 */
export function preprocessNumbersForSpeech(text: string): string {
  let processed = text;
  
  // Step 1: Handle real estate metric abbreviations FIRST (before other processing)
  // YoY → "year-on-year", MoM → "month-on-month", QoQ → "quarter-on-quarter"
  processed = processed.replace(/\bYoY\b/gi, 'year-on-year');
  processed = processed.replace(/\bMoM\b/gi, 'month-on-month');
  processed = processed.replace(/\bQoQ\b/gi, 'quarter-on-quarter');
  
  // Step 2: Handle approximate symbol (~) with numbers
  // ~12000 → "approximately twelve thousand"
  processed = processed.replace(/~(\d+(?:,\d{3})*(?:\.\d+)?)\b/g, (match, numStr) => {
    const cleanNum = numStr.replace(/,/g, '');
    const num = parseFloat(cleanNum);
    if (num <= 1000000000) {
      return `approximately ${numberToWords(num)}`;
    }
    return match;
  });
  
  // Step 3: Handle percentages with direction symbols (+ and -)
  // +5% → "up by five percent", -3% → "down by three percent"
  processed = processed.replace(/([+\-])\s*(\d+(?:\.\d+)?)%/g, (match, sign, numStr) => {
    const num = parseFloat(numStr);
    if (num < 1000) {
      const direction = sign === '+' ? 'up by' : 'down by';
      return `${direction} ${numberToWords(num)} percent`;
    }
    return match;
  });
  
  // Step 4: Handle percentages without direction (e.g., "25%" → "twenty-five percent")
  // Skip percentages that were already processed with + or - symbols in Step 3
  processed = processed.replace(/\b(\d+(?:\.\d+)?)%/g, (match, numStr, offset, fullString) => {
    // Check if there's a + or - before this percentage (within 5 characters)
    // This helps avoid double-processing (Step 3 already handled +5% and -3%)
    const before = fullString.substring(Math.max(0, offset - 5), offset);
    // If we see + or - immediately before, skip (it was already processed in Step 3)
    if (/[+\-]\s*$/.test(before.trim())) {
      return match;
    }
    const num = parseFloat(numStr);
    if (num < 1000) {
      return `${numberToWords(num)} percent`;
    }
    return match; // Keep large percentages as-is
  });
  
  // Step 5: Handle numbers with unit abbreviations (M, K, B for million, thousand, billion)
  // 1.25M → "one point two five million", 500K → "five hundred thousand"
  processed = processed.replace(/\b(\d+(?:\.\d+)?)\s*([KMkmB])\b/g, (match, numStr, unit) => {
    const num = parseFloat(numStr);
    const unitMap: { [key: string]: string } = {
      'K': 'thousand',
      'k': 'thousand',
      'M': 'million',
      'm': 'million',
      'B': 'billion',
      'b': 'billion'
    };
    const unitWord = unitMap[unit.toUpperCase()] || unit;
    
    // Convert the number part
    if (num % 1 === 0) {
      // Integer
      return `${numberToWords(num)} ${unitWord}`;
    } else {
      // Decimal - convert to words
      const parts = numStr.split('.');
      const intPart = parseInt(parts[0], 10);
      const decPart = parts[1];
      const decWords = decPart.split('').map((d: string) => digitToWord(d)).join(' ');
      return `${numberToWords(intPart)} point ${decWords} ${unitWord}`;
    }
  });
  
  // Step 6: Handle real estate context phrases for natural analyst-style narration
  // Enhance common real estate phrases to sound more natural
  
  // Metric trends: "prices are up 5%" → "prices are up five percent"
  // (percentages are already converted above, this ensures natural flow)
  processed = processed.replace(
    /\b(prices?|values?|rates?)\s+(are\s+)?(up|down|increased?|decreased?|rose|fell|grew|declined)\s+(\d+(?:\.\d+)?)\s*percent/gi,
    (match, metric, are, direction, percent) => {
      const num = parseFloat(percent);
      const directionWord = direction.toLowerCase();
      const naturalDirection = 
        directionWord.includes('up') || directionWord.includes('increase') || directionWord.includes('rose') || directionWord.includes('grew') 
          ? 'up' 
          : 'down';
      return `${metric} are ${naturalDirection} ${numberToWords(num)} percent`;
    }
  );
  
  // Transaction volume patterns
  processed = processed.replace(
    /\b(transaction\s+volume|sales\s+volume|deal\s+volume)\s+(is\s+)?(up|down|increased?|decreased?)\s+(\d+(?:\.\d+)?)\s*percent/gi,
    (match, metric, is, direction, percent) => {
      const num = parseFloat(percent);
      const naturalDirection = direction.toLowerCase().includes('up') || direction.toLowerCase().includes('increase') ? 'up' : 'down';
      return `${metric} is ${naturalDirection} ${numberToWords(num)} percent`;
    }
  );
  
  // Year-on-year, month-on-month patterns with percentages
  processed = processed.replace(
    /\b(up|down|increased?|decreased?|rose|fell|grew|declined)\s+(\d+(?:\.\d+)?)\s*percent\s+(year-on-year|month-on-month|quarter-on-quarter)/gi,
    (match, direction, percent, period) => {
      const num = parseFloat(percent);
      const naturalDirection = direction.toLowerCase().includes('up') || direction.toLowerCase().includes('increase') || direction.toLowerCase().includes('rose') || direction.toLowerCase().includes('grew') ? 'up' : 'down';
      return `${naturalDirection} ${numberToWords(num)} percent ${period}`;
    }
  );
  
  // Step 7: Handle years (4-digit numbers that look like years: 1900-2099)
  processed = processed.replace(/\b(19|20)\d{2}\b/g, (match) => {
    // Convert year to words (e.g., "2024" → "twenty twenty-four")
    const year = parseInt(match, 10);
    if (year >= 2000 && year < 2100) {
      const firstTwo = Math.floor(year / 100);
      const lastTwo = year % 100;
      if (lastTwo === 0) {
        return `${numberToWords(firstTwo)} hundred`;
      } else {
        return `${numberToWords(firstTwo)} ${numberToWords(lastTwo)}`;
      }
    } else if (year >= 1900 && year < 2000) {
      const lastTwo = year % 100;
      if (lastTwo === 0) {
        return 'nineteen hundred';
      } else {
        return `nineteen ${numberToWords(lastTwo)}`;
      }
    }
    return match;
  });
  
  // Step 8: Handle ordinal numbers (1st, 2nd, 3rd, 4th, etc.)
  const ordinals: { [key: number]: string } = {
    1: 'first', 2: 'second', 3: 'third', 4: 'fourth', 5: 'fifth',
    6: 'sixth', 7: 'seventh', 8: 'eighth', 9: 'ninth', 10: 'tenth',
    11: 'eleventh', 12: 'twelfth', 13: 'thirteenth', 14: 'fourteenth',
    15: 'fifteenth', 16: 'sixteenth', 17: 'seventeenth', 18: 'eighteenth',
    19: 'nineteenth', 20: 'twentieth', 30: 'thirtieth', 40: 'fortieth',
    50: 'fiftieth', 60: 'sixtieth', 70: 'seventieth', 80: 'eightieth',
    90: 'ninetieth'
  };
  
  processed = processed.replace(/\b(\d+)(st|nd|rd|th)\b/gi, (match, numStr, suffix) => {
    const num = parseInt(numStr, 10);
    if (ordinals[num]) {
      return ordinals[num];
    } else if (num < 100) {
      const tens = Math.floor(num / 10) * 10;
      const ones = num % 10;
      if (ones === 0 && ordinals[tens]) {
        return ordinals[tens];
      } else if (ones > 0 && ordinals[tens]) {
        return `${numberToWords(ones)}-${ordinals[tens]}`;
      }
    }
    return `${numberToWords(num)}${suffix}`;
  });
  
  // Step 9: Handle currency amounts (e.g., "$5000" → "five thousand dollars")
  const currencyMap: { [key: string]: string } = {
    '$': 'dollars',
    'USD': 'US dollars',
    '€': 'euros',
    'EUR': 'euros',
    '£': 'pounds',
    'GBP': 'British pounds',
    'AED': 'AED'
  };
  
  processed = processed.replace(/\b([$€£]|USD|EUR|GBP|AED)\s*(\d+(?:,\d{3})*(?:\.\d+)?)\b/gi, (match, currency, numStr) => {
    const cleanNum = numStr.replace(/,/g, '');
    const num = parseFloat(cleanNum);
    const currencyWord = currencyMap[currency.toUpperCase()] || currency;
    return `${numberToWords(num)} ${currencyWord}`;
  });
  
  // Step 10: Handle standalone numbers (not part of URLs, emails, or other contexts)
  // Match numbers that are:
  // - At word boundaries
  // - Not part of email addresses
  // - Not part of URLs
  // - Not already converted (don't contain words)
  processed = processed.replace(/\b(\d+(?:,\d{3})*(?:\.\d+)?)\b/g, (match, numStr, offset, fullText) => {
    // Skip if it's part of an email or URL
    const before = fullText.substring(Math.max(0, offset - 10), offset);
    const after = fullText.substring(offset + match.length, offset + match.length + 10);
    
    if (/[@.]/.test(before + after) || /https?:\/\//.test(fullText.substring(Math.max(0, offset - 7), offset + match.length + 7))) {
      return match;
    }
    
    // Skip if it's a year (already handled)
    if (/^\d{4}$/.test(numStr) && parseInt(numStr, 10) >= 1900 && parseInt(numStr, 10) <= 2099) {
      return match;
    }
    
    // Skip very large numbers that might be IDs or codes
    const cleanNum = numStr.replace(/,/g, '');
    const num = parseFloat(cleanNum);
    
    // Convert numbers up to 1 billion, skip larger ones (likely IDs)
    if (num > 1000000000) {
      return match;
    }
    
    // Skip single digits in certain contexts (they might be part of lists, etc.)
    if (num < 10 && /^[0-9]$/.test(numStr)) {
      // Only convert if it's clearly a standalone number
      const context = before.trim() + ' ' + after.trim();
      if (!/^[a-zA-Z]/.test(after.trim()) && !/[a-zA-Z]$/.test(before.trim())) {
        return numberToWords(num);
      }
      return match;
    }
    
    return numberToWords(num);
  });
  
  // Clean up multiple spaces
  processed = processed.replace(/\s+/g, ' ').trim();
  
  return processed;
}

